import type { BooleanRecord } from "@p-ban/common/src/types"

import ESTIMATE_CONSTANT from "../constant/estimateConstant"
import type EstimateValues from "../types/params"
import getNumberOfLayers from "./getNumberOfLayers"
import { isBuildUp, isDesign, isFlexible, isManufacture, isMetal, isMount, isMultiLayer, isRigid } from "./oneClickIs"

const getDisplayCondition = (values: EstimateValues) => {
  const numberOfLayers = getNumberOfLayers(values)

  const isDataServiceAvailable = values.dataService !== ESTIMATE_CONSTANT.データ面付け編集サービス.なし.value
  const isReflow = values.metalmask === ESTIMATE_CONSTANT.実装方法.リフロー実装をする.value

  const status: BooleanRecord<EstimateValues> = {
    kibanType: true,

    serviceD: true,
    serviceM: true,
    serviceG: true,
    partsProcurementCourse: true,

    metalBoard: isMetal(values),
    layer: true,
    patternSide: numberOfLayers === 1,
    sizeX: true,
    sizeY: true,
    fpcRectangularShape: isFlexible(values) && isManufacture(values),

    pinCnt: isDesign(values),
    planCadDataKbn: isDesign(values),
    fileKbn: isDesign(values),
    designInstruction: isDesign(values),

    cnt: isManufacture(values),

    sheetCnt: isMount(values),
    solderType: isMount(values),
    pieceCnt: isMount(values),
    bgacsp: isMount(values),
    sideOfSmd: isMount(values),
    dipPartCnt: isMount(values),
    sideOfDip: isMount(values),
    metalmask: isMount(values),
    provisionOfMountData: isMount(values) && isReflow,
    metalMaskDtl: isMount(values) && isReflow,
    freeMaterialsFlg: isMount(values),
    userProvPartFlg: isMount(values),
    materialsArrivalDate:
      isMount(values) &&
      (values.userProvPartFlg === ESTIMATE_CONSTANT.お客様支給部品.あり.value ||
        !isManufacture(values) ||
        values.metalMaskDtl === ESTIMATE_CONSTANT["メタルマスクの製造・支給選択"].お客様より支給あり.value),

    board: (isDesign(values) || isManufacture(values)) && (isRigid(values) || isFlexible(values)),
    boardThickness: (isDesign(values) || isManufacture(values)) && isRigid(values),
    fpcBoardThickness: (isDesign(values) || isManufacture(values)) && isFlexible(values),
    almThickness: (isDesign(values) || isManufacture(values)) && isMetal(values),
    beatenThickness: isDesign(values) || isManufacture(values),
    buildUpMethod: (isDesign(values) || isManufacture(values)) && isRigid(values) && isMultiLayer(values),

    minPattern: isDesign(values) || isManufacture(values),
    minLand: isDesign(values) || isManufacture(values),
    laserVia: (isDesign(values) || isManufacture(values)) && isBuildUp(values),
    externalsSilk: isDesign(values),
    controlWiring: isDesign(values),
    circuit: isDesign(values),
    planBgacsp: isDesign(values),
    otherSilk: isDesign(values),
    impedance: (isDesign(values) || isManufacture(values)) && isRigid(values),

    resistPrint: (isDesign(values) || isManufacture(values)) && (isRigid(values) || isMetal(values)),
    resistColor: (isDesign(values) || isManufacture(values)) && (isRigid(values) || isMetal(values)),

    coverLaySide: (isDesign(values) || isManufacture(values)) && isFlexible(values),
    coverLay: (isDesign(values) || isManufacture(values)) && isFlexible(values),
    coverLayColor: (isDesign(values) || isManufacture(values)) && isFlexible(values),

    silk: isDesign(values) || isManufacture(values),
    silkColor: isDesign(values) || isManufacture(values),
    silkPrintMethod: isManufacture(values) && isRigid(values),

    surfaceFinishing: isDesign(values) || isManufacture(values),
    hardGoldOnPCIEdgeTerminal: isManufacture(values) && isRigid(values),
    chamferingProcess: isManufacture(values) && isRigid(values),

    rfContactPortionCnt: isManufacture(values) && isFlexible(values),
    rfContactPortion1:
      values.rfContactPortionCnt === ESTIMATE_CONSTANT.端子部厚み指定["1箇所"].value ||
      values.rfContactPortionCnt === ESTIMATE_CONSTANT.端子部厚み指定["2箇所"].value,
    rfContactPortionBonding1:
      values.rfContactPortionCnt === ESTIMATE_CONSTANT.端子部厚み指定["1箇所"].value ||
      values.rfContactPortionCnt === ESTIMATE_CONSTANT.端子部厚み指定["2箇所"].value,
    rfContactPortion2: values.rfContactPortionCnt === ESTIMATE_CONSTANT.端子部厚み指定["2箇所"].value,
    rfContactPortionBonding2: values.rfContactPortionCnt === ESTIMATE_CONSTANT.端子部厚み指定["2箇所"].value,

    reinForcementCnt: isManufacture(values) && isFlexible(values),
    reinForcement1:
      values.reinForcementCnt === ESTIMATE_CONSTANT.補強板厚み指定["1箇所"].value ||
      values.reinForcementCnt === ESTIMATE_CONSTANT.補強板厚み指定["2箇所"].value,
    reinForcementBonding1:
      values.reinForcementCnt === ESTIMATE_CONSTANT.補強板厚み指定["1箇所"].value ||
      values.reinForcementCnt === ESTIMATE_CONSTANT.補強板厚み指定["2箇所"].value,
    reinForcement2: values.reinForcementCnt === ESTIMATE_CONSTANT.補強板厚み指定["2箇所"].value,
    reinForcementBonding2: values.reinForcementCnt === ESTIMATE_CONSTANT.補強板厚み指定["2箇所"].value,

    panelizedItemCnt: !isDesign(values) && isMount(values),
    panelizedItemKindCnt: !isDesign(values) && isMount(values),
    fpcSurfacePutData: !isDesign(values) && isManufacture(values) && isFlexible(values),
    vcutCnt: !isDesign(values) && isManufacture(values) && (isRigid(values) || isMetal(values)),
    jumpVcutCnt: !isDesign(values) && isManufacture(values) && (isRigid(values) || isMetal(values)),
    routerCut: !isDesign(values) && isManufacture(values) && (isRigid(values) || isMetal(values)),
    routerCutKind: !isDesign(values) && isManufacture(values) && (isRigid(values) || isMetal(values)),
    roulette: !isDesign(values) && isManufacture(values) && (isRigid(values) || isMetal(values)),
    rouletteKind: !isDesign(values) && isManufacture(values) && isMetal(values),
    dataService: !isDesign(values) && isManufacture(values) && (isRigid(values) || isMetal(values)),
    discardBoardMark: isDataServiceAvailable,
    recognizeMark: isDataServiceAvailable,
    editVariantSlit: isDataServiceAvailable,

    semiTh: (isDesign(values) || isManufacture(values)) && isRigid(values),
    longHole: isDesign(values) || isManufacture(values),
    padOnVia: (isDesign(values) || isManufacture(values)) && isRigid(values) && numberOfLayers >= 2,
    padLess03: (isDesign(values) || isManufacture(values)) && (isRigid(values) || isMetal(values)),
    emcShield: (isDesign(values) || isManufacture(values)) && isFlexible(values),

    ulMark: isDesign(values) || isManufacture(values),

    aoiInspection: isManufacture(values),
    manufactureInspectionCertificate: isManufacture(values),
    mountInspectionCertificate: isMount(values),

    dxfData: !isDesign(values) && isManufacture(values),
    manuOptPlanCad: !isDesign(values) && isManufacture(values),

    estManuFactoryId: isManufacture(values) && ((isRigid(values) && !isBuildUp(values)) || isFlexible(values)),
    rigidFlexPcb: isDesign(values),
    wireBonding: isDesign(values),
    awDataSupplied: isDesign(values),
    panelized: isDesign(values),
    ddrEmc: isDesign(values),
    designDaysShortening: isDesign(values),
    dxfDataOutput: isDesign(values),
  }

  return status
}

export default getDisplayCondition
